import { decodeUserDirectorySource } from './user-directory-source'
import {
  decodePage,
  isRecord,
  nullableString,
  request,
  requestNoContent,
  requireBoolean,
  requireNumber,
  requireString,
} from '@/lib/api'
import type { components } from '@/lib/openapi.generated'

type Schemas = components['schemas']

export type StartImpersonationResult = Schemas['StartImpersonationResponse']

function decodeStartImpersonation(raw: unknown): StartImpersonationResult {
  if (!isRecord(raw)) throw new Error('Malformed impersonation response')
  return {
    targetUserId: requireString(raw.targetUserId, 'targetUserId'),
    expiresInSeconds: requireNumber(raw.expiresInSeconds, 'expiresInSeconds'),
  } satisfies StartImpersonationResult
}

export type EntityUser = Schemas['EntityUserResponse']

export type UserAccessGrant = Schemas['UserAccessGrantResponse']

export type EntityUserRole = Schemas['EntityUserRoleResponse']

export type AccessAuthority = Schemas['AccessAuthorityDto']

function decodeAccessAuthority(raw: unknown): AccessAuthority {
  if (!isRecord(raw)) throw new Error('Malformed access authority')
  return {
    authority: requireString(raw.authority, 'authority'),
    displayName: requireString(raw.displayName, 'displayName'),
  } satisfies AccessAuthority
}

function decodeEntityUserRole(raw: unknown): EntityUserRole {
  if (!isRecord(raw) || !Array.isArray(raw.authorities))
    throw new Error('Malformed user role')
  return {
    roleId: requireString(raw.roleId, 'roleId'),
    name: requireString(raw.name, 'name'),
    isSystem: requireBoolean(raw.isSystem, 'isSystem'),
    viaGroupName: nullableString(raw.viaGroupName, 'viaGroupName'),
    viaGroupId: nullableString(raw.viaGroupId, 'viaGroupId'),
    viaGroupLinkEntityId: nullableString(raw.viaGroupLinkEntityId, 'viaGroupLinkEntityId'),
    authorities: raw.authorities.map(decodeAccessAuthority),
  } satisfies EntityUserRole
}

function decodeUserAccessGrant(raw: unknown): UserAccessGrant {
  if (!isRecord(raw) || !Array.isArray(raw.roles))
    throw new Error('Malformed user access grant')
  return {
    entityId: requireString(raw.entityId, 'entityId'),
    entityName: requireString(raw.entityName, 'entityName'),
    roles: raw.roles.map(decodeEntityUserRole),
  } satisfies UserAccessGrant
}

export function decodeUserAccessGrantsJson(value: string): UserAccessGrant[] {
  const raw: unknown = JSON.parse(value)
  if (!Array.isArray(raw)) throw new Error('Malformed user access grants')
  return raw.map(decodeUserAccessGrant)
}

function decodeEntityUser(raw: unknown): EntityUser {
  if (!isRecord(raw) || !Array.isArray(raw.grants))
    throw new Error('Malformed entity user')
  return {
    userId: requireString(raw.userId, 'userId'),
    name: requireString(raw.name, 'name'),
    email: nullableString(raw.email, 'email'),
    hasDirectGrant: requireBoolean(raw.hasDirectGrant, 'hasDirectGrant'),
    accessibleEntityCount: requireNumber(raw.accessibleEntityCount, 'accessibleEntityCount'),
    grants: raw.grants.map(decodeUserAccessGrant),
    active: requireBoolean(raw.active, 'active'),
    source: decodeUserDirectorySource(raw.source),
    title: nullableString(raw.title, 'title'),
    department: nullableString(raw.department, 'department'),
  } satisfies EntityUser
}

const decodeEntityUsersPage = (raw: unknown) =>
  decodePage(raw, decodeEntityUser, 'entity users page')

export type ServiceAccountSummary = Schemas['ServiceAccountSummaryDto']

function decodeServiceAccountSummary(raw: unknown): ServiceAccountSummary {
  if (!isRecord(raw)) throw new Error('Malformed service account')
  return {
    userId: requireString(raw.userId, 'userId'),
    name: requireString(raw.name, 'name'),
    description: nullableString(raw.description, 'description'),
    accessibleEntityCount: requireNumber(raw.accessibleEntityCount, 'accessibleEntityCount'),
  } satisfies ServiceAccountSummary
}

const decodeServiceAccountsPage = (raw: unknown) =>
  decodePage(raw, decodeServiceAccountSummary, 'service accounts page')

export type UserAccessProfileGrant = Schemas['UserAccessProfileGrantResponse']

export type UserAccessProfile = Schemas['UserAccessProfileResponse']

function decodeUserAccessProfileGrant(raw: unknown): UserAccessProfileGrant {
  if (!isRecord(raw) || !Array.isArray(raw.authorities))
    throw new Error('Malformed user access grant')
  return {
    entityId: requireString(raw.entityId, 'entityId'),
    entityName: requireString(raw.entityName, 'entityName'),
    roleId: requireString(raw.roleId, 'roleId'),
    roleName: requireString(raw.roleName, 'roleName'),
    viaGroupName: nullableString(raw.viaGroupName, 'viaGroupName'),
    viaGroupId: nullableString(raw.viaGroupId, 'viaGroupId'),
    viaGroupLinkEntityId: nullableString(raw.viaGroupLinkEntityId, 'viaGroupLinkEntityId'),
    authorities: raw.authorities.map(decodeAccessAuthority),
  } satisfies UserAccessProfileGrant
}

function decodeUserAccessProfile(raw: unknown): UserAccessProfile {
  if (!isRecord(raw) || !Array.isArray(raw.grants))
    throw new Error('Malformed user access profile')
  return {
    userId: requireString(raw.userId, 'userId'),
    name: requireString(raw.name, 'name'),
    email: nullableString(raw.email, 'email'),
    grants: raw.grants.map(decodeUserAccessProfileGrant),
  } satisfies UserAccessProfile
}

export type PendingUser = { userId: string; name: string; email: string | null }
export type PromoteAccessBinding = Schemas['AccessBindingInput']

export type CreateMachineAccountInput = Omit<
  Schemas['CreateMachineAccountRequest'],
  'description'
> & { description?: string }

export type MachineAccountDetail = Schemas['MachineAccountDetailResponse']
export type ServiceAccountCredential = Schemas['ServiceAccountCredentialResponse']
export type CreatedServiceAccountCredential = Schemas['CreateServiceAccountCredentialResponse']

export type UpdateMachineAccountInput = Omit<
  Schemas['UpdateMachineAccountRequest'],
  'description' | 'requestsPerMinute'
> & { description?: string; requestsPerMinute?: number }

function decodeServiceAccountCredential(raw: unknown): ServiceAccountCredential {
  if (!isRecord(raw)) throw new Error('Malformed service account credential')
  return {
    id: requireString(raw.id, 'id'),
    suffix: requireString(raw.suffix, 'suffix'),
    note: nullableString(raw.note, 'note'),
    createdAt: requireString(raw.createdAt, 'createdAt'),
    lastTokenIssuedAt: nullableString(raw.lastTokenIssuedAt, 'lastTokenIssuedAt'),
  } satisfies ServiceAccountCredential
}

function decodeCreatedServiceAccountCredential(raw: unknown): CreatedServiceAccountCredential {
  if (!isRecord(raw)) throw new Error('Malformed created service account credential')
  return {
    ...decodeServiceAccountCredential(raw),
    password: requireString(raw.password, 'password'),
  } satisfies CreatedServiceAccountCredential
}

function decodeMachineAccount(raw: unknown): MachineAccountDetail {
  if (!isRecord(raw) || !Array.isArray(raw.credentials) || !Array.isArray(raw.grants))
    throw new Error('Malformed machine account')
  return {
    userId: requireString(raw.userId, 'userId'),
    name: requireString(raw.name, 'name'),
    description: nullableString(raw.description, 'description'),
    clientId: requireString(raw.clientId, 'clientId'),
    creationDate: requireString(raw.creationDate, 'creationDate'),
    requestsPerMinute: requireNumber(raw.requestsPerMinute, 'requestsPerMinute'),
    credentials: raw.credentials.map(decodeServiceAccountCredential),
    grants: raw.grants.map(decodeUserAccessProfileGrant),
    publicApiUrl: requireString(raw.publicApiUrl, 'publicApiUrl'),
  } satisfies MachineAccountDetail
}

export type UserCandidate = Schemas['UserCandidateResponse']

function decodeUserCandidate(raw: unknown): UserCandidate {
  if (!isRecord(raw)) throw new Error('Malformed user candidate')
  return {
    userId: requireString(raw.userId, 'userId'),
    name: requireString(raw.name, 'name'),
    email: nullableString(raw.email, 'email'),
  } satisfies UserCandidate
}

const decodeUserCandidatesPage = (raw: unknown) =>
  decodePage(raw, decodeUserCandidate, 'user candidates page')

export const usersApi = {
  startImpersonation: (targetUserId: string) =>
    request('/api/v1/impersonation', decodeStartImpersonation, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ targetUserId } satisfies Schemas['StartImpersonationRequest']),
    }),
  stopImpersonation: () => requestNoContent('/api/v1/impersonation', { method: 'DELETE' }),
  getEntityUsers: (entityId: string, options?: { after?: string; query?: string; includeDeactivated?: boolean }) => {
    const query = new URLSearchParams()
    if (options?.includeDeactivated) query.set('includeDeactivated', 'true')
    if (options?.after) query.set('after', options.after)
    if (options?.query) query.set('q', options.query)
    const qs = query.toString()
    return request(`/api/v1/entities/${entityId}/users${qs ? `?${qs}` : ''}`, decodeEntityUsersPage)
  },
  getEntityMachineUsers: (entityId: string, options?: { after?: string; query?: string }) => {
    const query = new URLSearchParams()
    if (options?.after) query.set('after', options.after)
    if (options?.query) query.set('q', options.query)
    const qs = query.toString()
    return request(`/api/v1/entities/${entityId}/service-accounts${qs ? `?${qs}` : ''}`, decodeServiceAccountsPage)
  },
  createMachineAccount: (entityId: string, input: CreateMachineAccountInput) =>
    request(`/api/v1/entities/${entityId}/service-accounts`, (raw) => {
      if (!isRecord(raw)) throw new Error('Malformed machine account')
      return {
        userId: requireString(raw.userId, 'userId'),
      } satisfies Schemas['CreateMachineAccountResponse']
    }, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    }),
  getMachineAccount: (entityId: string, userId: string) =>
    request(`/api/v1/entities/${entityId}/service-accounts/${userId}`, decodeMachineAccount),
  createServiceAccountCredential: (entityId: string, userId: string, note?: string) =>
    request(
      `/api/v1/entities/${entityId}/service-accounts/${userId}/credentials`,
      decodeCreatedServiceAccountCredential,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: note || null } satisfies Schemas['CreateServiceAccountCredentialRequest']),
      },
    ),
  updateServiceAccountCredentialNote: (
    entityId: string,
    userId: string,
    credentialId: string,
    note?: string,
  ) => requestNoContent(
    `/api/v1/entities/${entityId}/service-accounts/${userId}/credentials/${credentialId}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note: note || null } satisfies Schemas['UpdateServiceAccountCredentialNoteRequest']),
    },
  ),
  revokeServiceAccountCredential: (
    entityId: string,
    userId: string,
    credentialId: string,
  ) => requestNoContent(
    `/api/v1/entities/${entityId}/service-accounts/${userId}/credentials/${credentialId}`,
    { method: 'DELETE' },
  ),
  updateMachineAccount: (
    entityId: string,
    userId: string,
    input: UpdateMachineAccountInput,
  ) => requestNoContent(`/api/v1/entities/${entityId}/service-accounts/${userId}`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input),
  }),
  deleteMachineAccount: (entityId: string, userId: string, name: string) =>
    requestNoContent(`/api/v1/entities/${entityId}/service-accounts/${userId}`, {
      method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name }),
    }),
  getUserCandidates: (entityId: string, options?: { after?: string; query?: string }) => {
    const query = new URLSearchParams()
    if (options?.after) query.set('after', options.after)
    if (options?.query) query.set('q', options.query)
    const qs = query.toString()
    return request(`/api/v1/entities/${entityId}/users/candidates${qs ? `?${qs}` : ''}`, decodeUserCandidatesPage)
  },
  getUserAccessProfile: (entityId: string, userId: string) =>
    request(`/api/v1/entities/${entityId}/users/${userId}/access`, decodeUserAccessProfile),
  getServiceAccountAccessProfile: (entityId: string, userId: string) =>
    request(`/api/v1/entities/${entityId}/service-accounts/${userId}/access`, decodeUserAccessProfile),
  promotePendingUser: (
    entityId: string,
    userId: string,
    bindings: PromoteAccessBinding[],
  ) =>
    requestNoContent(`/api/v1/entities/${entityId}/users/pending/${userId}/promote`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ bindings } satisfies Schemas['PromotePendingUserRequest']),
    }),
  rejectPendingUser: (entityId: string, userId: string) =>
    requestNoContent(`/api/v1/entities/${entityId}/users/pending/${userId}`, { method: 'DELETE' }),
  deleteUserAccess: (entityId: string, userId: string) =>
    requestNoContent(`/api/v1/entities/${entityId}/users/${userId}/roles`, { method: 'DELETE' }),
  assignUserRole: (entityId: string, userId: string, roleId: string) =>
    requestNoContent(`/api/v1/entities/${entityId}/users/${userId}/roles`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ roleId } satisfies Schemas['AssignRoleRequest']),
    }),
  assignServiceAccountRole: (entityId: string, userId: string, roleId: string) =>
    requestNoContent(`/api/v1/entities/${entityId}/service-accounts/${userId}/roles`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ roleId } satisfies Schemas['AssignRoleRequest']),
    }),
  revokeUserRole: (entityId: string, userId: string, roleId: string) =>
    requestNoContent(`/api/v1/entities/${entityId}/users/${userId}/roles/${roleId}`, { method: 'DELETE' }),
  revokeServiceAccountRole: (entityId: string, userId: string, roleId: string) =>
    requestNoContent(`/api/v1/entities/${entityId}/service-accounts/${userId}/roles/${roleId}`, { method: 'DELETE' }),
}
